import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminLayout from '../../components/layout/AdminLayout';
import blocksApi from "../../components/admin/blocksApi";

import toast from 'react-hot-toast';

const AdminBlocks = () => {
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [limit] = useState(2); // Set the limit per page
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const loadBlocks = async () => {
      try {
        setLoading(true);
        const response = await blocksApi.getBlocks({
          page: currentPage,
          limit: limit,
          search: searchTerm,
          status: filterStatus,
          sortBy,
          sortOrder
        });

        setBlocks(response.blocks || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalCount(response.pagination?.totalCount || 0);
      } catch (error) {
        console.error('Error loading blocks:', error);
        toast.error('Failed to load blocks');
        setBlocks([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    }

    loadBlocks();
  }, [currentPage, searchTerm, filterStatus, sortBy, sortOrder]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Removed status change functionality - only display status

  const handleAddNewBlock = () => {
    navigate('/admin/blocks/create');
  };

  const handleEditBlock = (blockId) => {
    navigate(`/admin/blocks/${blockId}/edit`);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-users">
          <div className="admin-card">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading blocks...</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-blocks">
        <div className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">Blocks Management</h2>
            <p className="admin-card-subtitle">Manage blocks ({totalCount} total)</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-card">
        <div className="admin-filters">
          <div className="admin-form-group">
            <label className="admin-form-label">Search Blocks</label>
            <input
              type="text"
              className="admin-form-input"
              placeholder="Search by title, identifier, or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Filter by Status</label>
            <select
              className="admin-form-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Blocks ({blocks.length} of {totalCount})</h3>
          <button 
            className="admin-btn admin-btn-primary"
            onClick={handleAddNewBlock}
          >
            ➕ Add New Block
          </button>
        </div>

        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('title')}
                  style={{ cursor: 'pointer' }}
                >
                  Title {sortBy === 'title' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('identifier')}
                  style={{ cursor: 'pointer' }}
                >
                  Identifier {sortBy === 'identifier' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('isActive')}
                  style={{ cursor: 'pointer' }}
                >
                  Active {sortBy === 'isActive' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('createdAt')}
                  style={{ cursor: 'pointer' }}
                >
                  Date {sortBy === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blocks.map((block) => (
                <tr key={block.id}>
                  <td>{block.id}</td>
                  <td>{block.title}</td>
                  <td>{block.identifier}</td>
                  <td>
                    <span 
                      className="admin-badge"
                      style={{ 
                        backgroundColor: block.isActive ? '#059669' : '#dc2626',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      {block.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{block.createdAt ? new Date(block.createdAt).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    <button
                      className="admin-btn admin-btn-secondary admin-btn-sm"
                      onClick={() => handleEditBlock(block.id)}
                      title="Edit Block"
                    >
                      ✏️ Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="admin-pagination">
            <div className="admin-pagination-info">
              Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalCount)} of {totalCount} blocks
            </div>
            <div className="admin-pagination-controls">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <div className="admin-pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      className={`admin-btn admin-btn-pagination ${
                        currentPage === pageNum ? 'active' : ''
                      }`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminBlocks;